<script lang="ts" setup>
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import AsyncData from '@sysarb/sysarb-api/models/common/AsyncData';
import type { PropType} from 'vue';
import { onMounted, ref, watch } from 'vue';
import CallOut from '@/components/common/CallOut.vue';
import { Status } from '@/components/common/editors/saveStatus/SaveStatuses';

/**********
 * Init
 **********/
const emit = defineEmits(['isLoaded', 'value']);

const props = defineProps({
    /** Determines the color of the loading icon. Defaults to green. */
    height: {
        type: String,
        default: '100%'
    },
    size: {
        type: String as PropType<'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'xxxlarge'>,
        default: 'xxlarge'
    },
    useOverlay: {
        type: Boolean,
        required: false
    },
    loadingMessage: {
        type: String,
        default: ''
    },
    centered: {
        type: Boolean,
        default: true
    },
    defaultLoadTime: {
        type: Number,
        default: 500
    },
    /** A value to automatically determine if this loading spinner should be shown or not. */
    value: {
        type: [Object, Boolean, Promise<unknown>, Array] as PropType<Promise<unknown> | Object | boolean | null | AsyncData<unknown>>,
        default: () => null
    }
});

const isLoading = ref<boolean>(true);
const timer = ref<any>();

/** If set an error message will be shown instead of the loading animation or child components. */
const errorMessage = ref<string | null>(null);

function setLoading(loading: boolean): void {
    isLoading.value = loading;
}

/** Sets the loadingstate if the data is done loading it emits a message. This makes sure that if a loading sequence is smaller than defaultLoadTime then we load for atleast that time before showing the data.
 * This is done purely to remove flickers in the design when data loads very quick and shows and removes the loader almost instantly.
 * @param isLoading:
 */
function setLoadingState(isLoading: boolean): void {
    const interval = 100;
    if (isLoading) {
        setLoading(isLoading);
        timer.value = setInterval(() => {}, interval);
    } else {
        if (timer.value <= props.defaultLoadTime) {
            setTimeout(setLoading, props.defaultLoadTime - timer.value, isLoading);
        } else {
            setLoading(isLoading);
        }
        clearInterval(timer.value);
        emit('isLoaded');
        emit('value', isLoading);
    }
}

function refreshIsLoading(): void {
    errorMessage.value = null;

    // If the model is null or undefined show the loading spinner
    if (props.value == null) {
        setLoadingState(true);
        return;
    }

    if (props.value instanceof AsyncData) {
        // An AsyncData must be finished loading.
        const asyncData = props.value as AsyncData<any>;
        setLoadingState(asyncData.isLoading);

        // If the AsyncData object has not finished loading,
        // wait for its promise to resolve
        asyncData.promise.finally(() => {
            // If the data loaded correctly, remove the loading and show the content
            setLoadingState(false);
        }).catch(err => {
            // If an error occurred, show the error message instead
            errorMessage.value = err.message;
        });
    } else if (typeof props.value === 'boolean') {
        // A boolean value determines by true or false
        setLoadingState((props.value));
    } else if (props.value instanceof Error) {
        // An error value shows an error message
        setLoadingState(false);
        errorMessage.value = (props.value).message;
    } else if (props.value instanceof Promise) {
        // If the value is a Promise,
        // hide the loading spinner when it is finished (success or error)
        const promiseModel = props.value;
        setLoadingState(true);

        // When the value resolves or gets an error,
        // stop showing the error
        promiseModel.finally(() => {
            // If the data loaded correctly, remove the loading and show the content
            setLoadingState(false);
        }).catch(err => {
            // If an error occurred, show the error message instead
            errorMessage.value = err.message;
        });
    } else {
        // If the object is not null, hide the loading spinner
        setLoadingState(false);
    }
}

watch(async () => props.value, () => {
    refreshIsLoading();
}, { deep: true });

onMounted(() => {
    refreshIsLoading();
});

</script>

<template>
    <div v-if="useOverlay" class="position-relative" :style="{ height: height }">
        <div v-if="isLoading" :class="['sy-loading-overlay flex-container', { 'loading': isLoading }, { 'align-center-middle': centered }]">
            <slot name="overlay">
                <LoadingSpinner
                    :size="size"
                    :loading-message="loadingMessage"
                />
            </slot>
        </div>
        <slot></slot>
    </div>
    <template v-else>
        <CallOut v-if="errorMessage != null" :status="Status.Error">{{ errorMessage }}</CallOut>
        <LoadingSpinner
            v-else-if="isLoading"
            :style="{ height }"
            :size="size"
            :loading-message="loadingMessage"
        />
        <slot v-else></slot>
    </template>
</template>

<style lang="scss" scoped>
.sy-loading-overlay {
    background-color: transparent;
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 10;
    transition: all ease 0.3s;
    &.loading {
        background-color: rgba(255, 255, 255, 0.3);
    }
}
</style>
